<script lang="ts">
	//Services
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { groups } from 'src/stores/lists/groups';
import { app } from 'src/stores/session/native-bridge';
	//Helpers
	import { getDevicesWithItsChannels, real_id } from '../../../../helpers/interface_helpers';

	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { checkMode } from '../../../../helpers/mode_switch_helpers';
	import {
		checkSharedChannels,
		unshareAllChannels,
		type SharedChannelData,
	} from '../../../../helpers/profile_change_shared_helper';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, ButtonGroupV2, Container, Modal, Text } from '@Shelly/shelly-design-system';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { resetDrawer, resetDrawerClose } from 'src/helpers/drawer_helpers';
	//Components
	import ProfileChangeSharedModal from './ProfileChangeSharedModal.svelte';
	import DeviceProfileHelp from './DeviceProfileHelp.svelte';
	import SettingGuide from '../../SettingGuide.svelte';

	export let device;
	const id = real_id(device.id);

	const options = ['cover', 'switch'];
	const i18n_options = {
		cover: $format('label_cover', { default: 'cover' }),
		switch: $format('label_switch', { default: 'switch' }),
	};
	$: settingsStore = getDeviceSettings(id);
	$: settings = $settingsStore;

	let value: string | undefined;
	let cache: string | undefined;

	$: deviceProfile = settings?.sys?.device?.profile;
	$: if (
		typeof deviceProfile === 'string' &&
		options.includes(deviceProfile) &&
		cache === value
	) {
		value = deviceProfile;
		cache = deviceProfile;
	}
	let loading = false;
	let showModal: boolean = false;
	let showConfirmation: boolean = false;
	let showSharedModal: boolean = false;
	let sharedChannelsData: SharedChannelData[] = [];
	let isSaveDisabled = true;

	$: isSaveDisabled = loading || cache === value;

	const handleSave = async (device) => {
		if (cache === value) return;
		await proceedToConfirmation(device);
	};

	const proceedToConfirmation = async (device) => {
		const channelsData = await checkSharedChannels(device);

		if (channelsData.length > 0) {
			sharedChannelsData = channelsData;
			showSharedModal = true;
			return;
		}

		const devicesWithChannels = getDevicesWithItsChannels(device);

		const concatenatedDevices: string[] = Object.values($groups).reduce<string[]>(
			(res, obj: any) => {
				res = res.concat(obj.devices);
				return res;
			},
			[],
		);

		const uniqueDevices = [...new Set(concatenatedDevices)];

		const inGroup = uniqueDevices.filter((deviceId: string) =>
			devicesWithChannels.includes(deviceId),
		);

		if (inGroup.length !== 0) {
			showModal = true;
		} else {
			showConfirmation = true;
		}
	};

	const sendReq = async () => {
		if (cache === value) return;
		loading = true;
		showModal = false;
		showConfirmation = false;
		showSharedModal = false;

		await unshareAllChannels(sharedChannelsData);

		const params = {
			id: device.channel,
			name: options[options.indexOf(value)],
		};

		const res: SettingRequest = await gen2Request(device, 'Shelly.SetProfile', params);

		const response = onSettingsResponse(res, device);

		if (response?.isok) {
			if ($app) {
				resetDrawerClose();
			} else {
				resetDrawer();
			}
			settings.sys.device.profile = options[options.indexOf(value)];
			cache = value;
		} else {
			value = cache;
		}
		await checkMode(device.id, settings);
		loading = false;
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: () => handleSave(device),
				loading,
				disabled: isSaveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<SettingGuide
	text={$format('settings_guide_device_profile', {
		default:
			'Choose how this device should operate. Changing the profile restarts the device and clears all existing schedules and actions.',
	})}
/>

<Container padding="s" type="borderSolid" backgroundColor="color-base">
	<ButtonGroupV2
		class="mb-s"
		id="buttongroup-device-profile"
		aria-label={$format('device_profile', { default: 'Device profile' })}
		fullWidth
		noWrap
		orientation="horizontal"
	>
		{#each options as option}
			<Button
				class={value === option ? 'active' : undefined}
				size="m"
				on:click={() => (value = option)}
			>
				{i18n_options[option]}
			</Button>
		{/each}
	</ButtonGroupV2>
	<DeviceProfileHelp profile={value ?? ''} />
</Container>

{#if sharedChannelsData.length > 0}
	<ProfileChangeSharedModal bind:showModal={showSharedModal} on:confirm={sendReq} />
{/if}

{#if !$showNewLayout}
	<Button
		class="mt-s"
		{loading}
		disabled={isSaveDisabled}
		size="m"
		width="full"
		on:click={() => handleSave(device)}>{$format('button_save')}</Button
	>
{/if}

<Modal
	on:close={() => (showConfirmation = false)}
	showModal={showConfirmation}
	title={$format('label_confirmation', {
		default: 'Confirmation',
	})}
	backdropClose={true}
>
	<Container slot="content" class="p-s" padding="0">
		<Text>
			{$format('text_confirmation_change_cover_profile', {
				default:
					'Changing the device profile will delete all webhooks and schedules on the device. Are you sure you want to proceed?',
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => (showConfirmation = false)}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="primary"
			on:click={sendReq}>{$format('button_confirm', { default: 'Confirm' })}</Button
		>
	</div>
</Modal>

<Modal
	on:close={() => (showModal = false)}
	{showModal}
	title={$format('modal_title_remove_device_from_group', {
		default: 'Remove device from groups',
	})}
	backdropClose={true}
>
	<Container slot="content" class="p-s" padding="0">
		<Text>
			{$format('text_modal_remove_device_from_group_description', {
				default: 'The device will be removed from group after changing the profile.',
			})}
		</Text>
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			variant="secondary"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => (showModal = false)}
		>
			{$format('button_cancel')}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="danger"
			on:click={sendReq}>{$format('button_remove', { default: 'Remove' })}</Button
		>
	</div>
</Modal>
